import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { BedroomOption, CountryGroupItem, LeaseOption, ReadCountryHotelGroupListResponse, ReadLeaseLengthResponse, ReadNoOfBedResponse } from '../../core/api/api.models';
import { UiService } from '../../core/ui.service';
import { SearchAvailabilityQuery } from '../../core/models/booking-model';
import { MultiSelect } from 'primeng/multiselect';
import { LogService } from '../../core/log.service';
import { ActivatedRoute, Router } from '@angular/router';
import { BtrBookingService } from '../../core/services/btr-booking.service';
import { getQuestions } from '../../data/data';
import dayjs from 'dayjs';
import { forkJoin } from 'rxjs';
import { Dropdown } from '../../ui-components/dropdown/dropdown/dropdown';
import { Calendar } from '../../ui-components/calendar/calendar/calendar';
import { utc_date } from '../../utilities/date-utils';
import { APP_CONFIG } from '../../core/config/app-config.service';
import { IAppConfig } from '../../core/config/app-config.model';

export interface OptionItem {
  name: string,
  code: string | number,
}

@Component({
  selector: 'app-header-search-bar',
  standalone: false,
  templateUrl: './header-search-bar.component.html',
  styleUrls: ['./header-search-bar.component.sass']
})
export class HeaderSearchBarComponent implements OnInit {
  @ViewChild('propertyComp') propertyComp?: Dropdown;
  @ViewChild('leaseComp') leaseComp?: Dropdown;
  @ViewChild('arrivalDateComp') arrivalDateComp?: Calendar;
  @ViewChild('bedRoomsComp') bedRoomsComp?: MultiSelect;
  @ViewChild('bathRoomsComp') bathRoomsComp?: MultiSelect;

  @ViewChild('propertyCompDialog') propertyCompDialog?: Dropdown;
  @ViewChild('leaseCompDialog') leaseCompDialog?: Dropdown;
  @ViewChild('arrivalDateCompDialog') arrivalDateCompDialog?: Calendar;
  @ViewChild('bedRoomsCompDialog') bedRoomsCompDialog?: MultiSelect;

  @Output('bookAnInspection') bookAnInspection = new EventEmitter();

  currentDate = new Date();

  setOptionFilter: {
    visible: boolean
  } = {
    visible: false
  }

  searchOption: {
    propertyCode: OptionItem[],
    numberOfBathrooms: OptionItem[],
    numberOfBedrooms: OptionItem[],
    furnished: OptionItem[],
    priceRange: OptionItem[],
    promotionCode: string,
    lease: OptionItem[]
  } = {
      propertyCode: [],
      numberOfBathrooms: [],
      numberOfBedrooms: [],
      furnished: [],
      priceRange: [],
      lease: [],
      promotionCode: ''
    };

    searchFilter: {
      propertyCode: OptionItem | null,
      lease: OptionItem | null,
      arrivalDate: Date
      numberOfBathrooms: OptionItem[],
      numberOfBedrooms: OptionItem[],
      furnished: OptionItem | null,
      priceRange: OptionItem | null,
      promotionCode: string,
      applicationID: string | null
    } = {
        propertyCode: null,
        lease: null,
        arrivalDate: new Date(Date.now() + ( 3600 * 1000 * 24)),
        numberOfBathrooms: [],
        numberOfBedrooms: [],
        furnished: null,
        priceRange: null,
        promotionCode: '',
        applicationID: null
      }
    
    disabledStartDates: Date[] = [];

    hotels: CountryGroupItem[] = [];
    noBedRoom: BedroomOption[] = [];
    leaseLength: LeaseOption[] = [];

    searchAvailabilityQuery: SearchAvailabilityQuery | undefined | null;

    constructor(
      private btrBookingService: BtrBookingService,
      private router: Router,
      private log: LogService,
      private ui: UiService,
      private route: ActivatedRoute,
      @Inject(APP_CONFIG) protected config: IAppConfig,
    ) {
  
    }

    ngOnInit(): void {
      this.currentDate.setHours(0, 0, 0, 0);
      const minimumStartDate = this.btrBookingService.getMinimumLeaseEndDateConfig();
      if(minimumStartDate && this.currentDate.getTime() < minimumStartDate.getTime()){
        this.currentDate = minimumStartDate;
        this.searchFilter.arrivalDate = new Date(minimumStartDate);
      }

      this.getHotelGroupList();

    }
  
    ngOnDestroy(): void {
  
    }
  
    getHotelGroupList() {
      this.btrBookingService.readCountryHotelGroupList().subscribe({
        next: (res) => {
          if (res && res.success && res.countryGroups && res.countryGroups.length) {
            res.countryGroups.forEach(group => {
              if (group.items && group.items.length) {
                group.items.forEach(item => {
                  this.hotels.push(item);
                })
              }
            })
            forkJoin({
              getNoBedRoom: this.btrBookingService.readNoOfBedroom(),
              getLeaseLength: this.btrBookingService.readLeaseLength(),
            }).subscribe(res => {
              this.readNoOfBedSuccess(res.getNoBedRoom);
              this.readLeaseLength(res.getLeaseLength);
              this.setSearchOption();
              this.route.queryParams.subscribe((params: any) => {
                this.loadSearchAvailabilityQuery(params);
              });
            })
          }
        }
      })
    }

    readNoOfBedSuccess(res: ReadNoOfBedResponse) {
      if (res && res.success && res.items) {
        this.noBedRoom = res.items.sort((a, b) => a.order - b.order);
      }
    }
  
    readLeaseLength(res: ReadLeaseLengthResponse) {
      if (res && res.success && res.items) {
        this.leaseLength = res.items.sort((a, b) => a.order - b.order);
      }
    }
  
    readHotelGroupSuccess(res: ReadCountryHotelGroupListResponse) {
      if (res && res.success && res.countryGroups && res.countryGroups.length) {
        res.countryGroups.forEach(group => {
          if (group.items && group.items.length) {
            group.items.forEach(item => {
              this.hotels.push(item);
            })
          }
        })
        this.setSearchOption();
        this.route.queryParams.subscribe((params: any) => {
          this.loadSearchAvailabilityQuery(params);
        });
      }
    }
  
    setSearchOption() {
      const questions = getQuestions();
      const bedRoomData = questions.find(item => item.name === 'bedrooms');
      const bathRoomData = questions.find(item => item.name === 'bathrooms');
      const furnishedData = questions.find(item => item.name === 'furnished');
      const priceRangeData = questions.find(item => item.name === 'price_range');
      const leaseData = questions.find(item => item.name === 'lease');
      this.searchOption.propertyCode = this.hotels.map(hotel => ({ name: hotel.hotelName, code: hotel.hotelRefID }));
      this.searchOption.numberOfBedrooms = this.noBedRoom.length ? this.noBedRoom.map(op => ({ name: op.name, code: op.value })) : (bedRoomData && bedRoomData.options) ? bedRoomData.options.map(op => ({ name: op.title, code: op.value })) : [];
      this.searchOption.numberOfBathrooms = (bathRoomData && bathRoomData.options) ? bathRoomData.options.map(op => ({ name: op.title, code: op.value })) : [];
      this.searchOption.furnished = (furnishedData && furnishedData.options) ? furnishedData.options.map(op => ({ name: op.title, code: op.value })) : [];
      this.searchOption.priceRange = (priceRangeData && priceRangeData.options) ? priceRangeData.options.map(op => ({ name: op.title, code: op.value })) : [];
      this.searchOption.lease = this.leaseLength.length ? this.leaseLength.map(op => ({ name: op.name, code: op.value })) : (leaseData && leaseData.options) ? leaseData.options.map(op => ({ name: op.title, code: op.value })) : [];
    }
  
    loadSearchAvailabilityQuery(params: any) {
      let firstAvailableDate = this.getFirstAvailableDate(params.property);
      this.searchAvailabilityQuery = this.btrBookingService.parseSearchAvailabitity(params, firstAvailableDate);
      if (this.searchAvailabilityQuery) {
        if(this.searchAvailabilityQuery.redirectAction == 'sitetour'){
          this.setDefaultSearchUrl();
          return;
        } else if (this.searchAvailabilityQuery.redirectAction == 'bookInspection') {
          this.bookAnInspection.emit();
          this.removeQueryParam('redirectAction')
          return;
        }
        this.setQuerySearchOption()
      }
    }

    removeQueryParam(paramKey: string) {
      const currentUrl = this.router.url.split('?')[0];
      const queryParams = { ...this.route.snapshot.queryParams };
      delete queryParams[paramKey];
      this.router.navigate([currentUrl], { queryParams });
    }

    setDefaultSearchUrl() {
      if(!this.hotels[0]?.hotelRefID){
        return;
      }

      this.searchFilter.arrivalDate = this.getFirstAvailableDate(this.hotels[0]?.hotelRefID)
      const param: SearchAvailabilityQuery = {
        property: this.hotels[0]?.hotelRefID,
        leaseLength: 12,
        fromDate: this.searchFilter.arrivalDate,
        bathRooms: 0,
        bedRooms: this.noBedRoom.length ? this.noBedRoom.map(i => i.value).join(',').toString() : 0
      };
      const queryString = this.btrBookingService.createSearchAvailabilityQueryString(param);
      this.router.navigateByUrl(`/apartments/results?${queryString}`);
    }

    changePropertyCode(event: any) {
      if(!event.value?.code) return;
      this.searchFilter.arrivalDate = this.getFirstAvailableDate(event.value.code);
      this.buildDisabledStartDates(this.searchFilter.arrivalDate);
      if(this.hotels && this.hotels.length){
        const hotelConfig = this.hotels.find(e => e.hotelRefID == event.value.code);
        if(hotelConfig){
          this.btrBookingService.updateCurrentProperty(hotelConfig);
        }
      }
    }

    getFirstAvailableDate(propertyCode: string) : Date {
      let firstDate = new Date();
      const minimumStartDate = this.btrBookingService.getMinimumLeaseEndDateConfig(propertyCode);
      if(minimumStartDate && firstDate.getTime() < minimumStartDate.getTime()){
        firstDate = new Date(minimumStartDate);
        this.currentDate = new Date(minimumStartDate);
      } else {
        this.currentDate = new Date();
      }
      
      const hotelConfig = this.hotels.find(e => e.hotelRefID == propertyCode);
      if(!hotelConfig){
        return firstDate
      }

      const now = new Date();
      if(hotelConfig.numbersOfDaysAddedToToday){
        const d = new Date();
        d.setDate(d.getDate() + hotelConfig.numbersOfDaysAddedToToday);
        (firstDate < d) && (firstDate = d);
      }
  
      if(hotelConfig.leaseStartFirstDateOfMonth === 1){
        if(firstDate.getDate() !== 1) {
          firstDate = new Date(firstDate.getFullYear(), firstDate.getMonth() + 1, 1);
        }
        return firstDate;
      }
      else if(hotelConfig.leaseStartFirstDateOfMonth === 2){
        if(firstDate.getFullYear() !== now.getFullYear() || firstDate.getMonth() !== now.getMonth()){
          // firstDate = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
          if(firstDate.getDate() !== 1) {
            firstDate = new Date(firstDate.getFullYear(), firstDate.getMonth() + 1, 1);
          }
          return firstDate;
        }
      }
      return firstDate;
    }

    setQuerySearchOption() {
      if (this.searchAvailabilityQuery?.property) {
        const hotelRefID = this.searchAvailabilityQuery.property;
        const hotelData = this.hotels.find(hotel => hotel.hotelRefID == hotelRefID);
        this.btrBookingService.updateCurrentProperty(hotelData);
        this.searchFilter.propertyCode = hotelData ? { code: hotelData.hotelRefID, name: hotelData.hotelName } : null;
      }
      if (this.searchAvailabilityQuery?.leaseLength) {
        let lease = this.searchAvailabilityQuery?.leaseLength.toString();
        let leaseData = this.searchOption.lease.find(opt => opt.code == lease);
        if(leaseData) {
          this.searchFilter.lease = leaseData;
        } else if (parseInt(lease)) {
          this.searchOption.lease.push({ name: `${lease} months`, code: Number(lease)});
          this.searchOption.lease = this.searchOption.lease.sort((a, b) => Number(a.code) - Number(b.code));
          leaseData = this.searchOption.lease.find(opt => opt.code == lease);
          (leaseData) && (this.searchFilter.lease = leaseData)
        }
      }
      if (this.searchAvailabilityQuery?.fromDate) {
        let date = new Date(this.searchAvailabilityQuery.fromDate);
        // date.setHours(9);
        this.searchFilter.arrivalDate = date;
        this.buildDisabledStartDates(this.searchFilter.arrivalDate);
      }
      if (this.searchAvailabilityQuery?.bedRooms !== undefined) {
        const arrBed = this.searchAvailabilityQuery.bedRooms.toString().split(',');
  
        const temp: OptionItem[] = [];
        arrBed.forEach(bed => {
          const bedData = this.searchOption.numberOfBedrooms.find(opt => opt.code == bed)
          bedData && temp.push(bedData)
        })
        this.searchFilter.numberOfBedrooms = [...temp]
      }
      if (this.searchAvailabilityQuery?.bathRooms) {
        const arrBath = this.searchAvailabilityQuery.bathRooms.toString().split(',');
        const temp: OptionItem[] = [];
        arrBath.forEach(bath => {
          const bathData = this.searchOption.numberOfBathrooms.find(opt => opt.code == bath)
          bathData && temp.push(bathData)
        })
        this.searchFilter.numberOfBathrooms = [...temp]
      }
      if (this.searchAvailabilityQuery?.furnished) {
        const furnished = this.searchAvailabilityQuery?.furnished;
        const furnishedData = this.searchOption.furnished.find(opt => opt.code == furnished);
        furnishedData && (this.searchFilter.furnished = furnishedData);
      }
      if (this.searchAvailabilityQuery?.priceRange) {
        const priceRange = this.searchAvailabilityQuery?.priceRange;
        const priceRangeData = this.searchOption.priceRange.find(opt => opt.code == priceRange);
        priceRangeData && (this.searchFilter.priceRange = priceRangeData);
      }
      if (this.searchAvailabilityQuery?.promotionCode) {
        const promotionCode = this.searchAvailabilityQuery?.promotionCode;
        promotionCode && (this.searchFilter.promotionCode = promotionCode);
      }
      if (this.searchAvailabilityQuery?.applicationID) {
        const applicationID = this.searchAvailabilityQuery.applicationID;
        applicationID && (this.searchFilter.applicationID = applicationID);
      }
    }

    onStartDateChange(){
      console.log(`Start date: ${JSON.stringify(this.searchFilter.arrivalDate)}`);
    }

    onStartDateMonthChange(event: any){
      this.log.log(`On month change ${event.month}-${event.year}`);
      if(!event.year || !event.month){
        return;
      }
      this.buildDisabledStartDates(new Date(event.year, event.month - 1, 1))
    }

    buildDisabledStartDates(month: Date){
      const hotelConfig = this.hotels.find(e => e.hotelRefID == this.searchFilter.propertyCode?.code);
      if(!hotelConfig){
        this.disabledStartDates = [];
        return;
      }
  
      const dis = new Date(month.getFullYear(), month.getMonth(), 5);
      const disabledStartDates: Date[] = [];
      const now = new Date();
  
      if(hotelConfig.numbersOfDaysAddedToToday){
        for(let i = 0; i < hotelConfig.numbersOfDaysAddedToToday; i++){
          const d = new Date();
          d.setDate(d.getDate() + i);
          disabledStartDates.push(d);
        }
      }
  
      if(hotelConfig.leaseStartFirstDateOfMonth === 1){
        for(let i = 1; i <= 31; i++){
          if(i !== 1){
            const d = new Date(month.getFullYear(), month.getMonth(), i);
            disabledStartDates.push(d);
          }
        }
      }
      else if(hotelConfig.leaseStartFirstDateOfMonth === 2){
        if(month.getFullYear() !== now.getFullYear() || month.getMonth() !== now.getMonth()){
          for(let i = 1; i <= 31; i++){
            if(i !== 1){
              const d = new Date(month.getFullYear(), month.getMonth(), i);
              disabledStartDates.push(d);
            }
          }
        }
      }
  
      this.disabledStartDates = disabledStartDates;
    }

    getValue(value: any[]) {
      return value && value.length === this.searchOption.numberOfBedrooms.length ? [] : value;
    }

    setParamQuery() {
      const { propertyCode, lease, numberOfBathrooms, numberOfBedrooms, furnished, arrivalDate, priceRange, promotionCode, applicationID } = this.searchFilter;
      const fromDate = new Date(arrivalDate);
      const hotelConfig = this.hotels.find(e => e.hotelRefID === propertyCode?.code);
      if(hotelConfig){
        if(hotelConfig.leaseStartFirstDateOfMonth === 1 && fromDate.getDate() !== 1){
          // TODO: translate
          this.ui.toastError('Start date must be the first date of the month.');
          return;
        }
        else if(hotelConfig.leaseStartFirstDateOfMonth === 2){
          const now = new Date();
          if(fromDate.getDate() !== 1 && (fromDate.getMonth() !== now.getMonth() || fromDate.getFullYear() !== now.getFullYear())){
            // TODO: translate
            this.ui.toastError('Start date must be the first date of the next month.');
            return;
          }
        }
      }
      else{
        this.log.log(`setParamQuery failed to read hotel config`);
      }
      if (propertyCode && numberOfBedrooms?.length && arrivalDate) {
        this.setOptionFilter.visible = false;
        const params: SearchAvailabilityQuery = {
          property: propertyCode.code.toString(),
          leaseLength: Number(lease?.code || 12), 
          fromDate: fromDate,
          bedRooms: numberOfBedrooms.map(bed => bed.code).join(','),
          // bathRooms: numberOfBathrooms.map(bed => bed.code).join(','),
          furnished: (furnished && furnished.code) ? furnished.code.toString() : '',
          priceRange: (priceRange && priceRange.code) ? priceRange.code.toString() : '',
          promotionCode: promotionCode,
          applicationID: applicationID || '',
        };
        promotionCode && (params.promotionCode = promotionCode);
        const queryString = this.btrBookingService.createSearchAvailabilityQueryString(params);
  
        this.router.navigateByUrl(`/apartments/results?${queryString}`);
      } else {
        this.checkValidSearch()
      }
    }

    checkValidSearch() {
      const { propertyCode, numberOfBedrooms, lease, furnished, priceRange, arrivalDate } = this.searchFilter
      if (!propertyCode) {
        this.propertyComp && this.propertyComp.show();
        this.propertyCompDialog && this.propertyCompDialog.show();
        return;
      } else if (!numberOfBedrooms?.length) {
        this.bedRoomsComp && this.bedRoomsComp.show();
        this.bedRoomsCompDialog && this.bedRoomsCompDialog.show();
        return;
      } else if(!lease) {
        this.leaseComp && this.leaseComp.show();
        this.leaseCompDialog && this.leaseCompDialog.show();
      } else if (!arrivalDate) {
        this.arrivalDateComp && this.arrivalDateComp.showOverlay();
        this.arrivalDateCompDialog && this.arrivalDateCompDialog.showOverlay();
        return;
      }
    }

    openDialogOption(type: 'property' | 'numOfBed' | 'lease' | 'arrivalDate') {
      switch(type) {
        case 'property': 
          this.propertyComp && this.propertyComp.show();
          break;
        case 'numOfBed':
          this.bedRoomsComp && this.bedRoomsComp.show();
          break;
        case 'lease': 
          this.leaseComp && this.leaseComp.show();
          break;
        case 'arrivalDate':
          this.arrivalDateComp && this.arrivalDateComp.toggle();
          return;
        default: 
          break;
      }
    }

    hideDialogOption(type: 'numOfBed') {
      switch(type) {
        case 'numOfBed':
          this.bedRoomsComp && this.bedRoomsComp.hide();
          this.bedRoomsCompDialog && this.bedRoomsCompDialog.hide();
          break;
      }
    }

    openSetOptionFilter() {
      this.setOptionFilter.visible = true;
    }

    getOptionFilterTitle(type: string){
      if(type == 'property'){
        return this.searchFilter.propertyCode?.name || 'Location';
      } else if (type == 'lease'){
        return this.searchFilter.lease?.name || 'Lease';
      } else if (type == 'bedroom') {
        const selectLength = this.searchFilter.numberOfBedrooms?.length || 0;
        return selectLength ? (selectLength == this.searchOption.numberOfBedrooms?.length ? 'All' : this.searchFilter.numberOfBedrooms.map(item => item.name).join(',')): 'Bedrooms';
      } else if (type == 'promotion') {
        return this.searchFilter.promotionCode || 'Promotion';
      } else if (type == 'startDate') {
        return this.searchFilter.arrivalDate ? dayjs(this.searchFilter.arrivalDate).format('DD MMM YY') : 'Arrival';
      }

      return '';
    }

    isHidePromoCode() {
      return this.config.showPromoCodeOnResultPage == false;
    }
}